import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from '../api';

/**
 * useStudentRoster
 *
 * Custom hook for fetching, filtering, and sorting student roster data.
 * Handles all data transformation and state management for the roster table.
 *
 * Supports two modes:
 * - tutorId = number: Fetch students assigned to specific tutor
 * - tutorId = 'all': Fetch ALL students with assignment status (admin only)
 */

// Default filter state
const DEFAULT_FILTERS = {
  subject: '',
  searchTerm: '',
  dateFrom: null,
  dateTo: null,
  assignmentStatus: '' // 'assigned', 'unassigned', or '' for all
};

// Default sort configuration
const DEFAULT_SORT = {
  field: 'assigned_at',
  direction: 'desc'
};

const useStudentRoster = (tutorId) => {
  // Raw data from API
  const [students, setStudents] = useState([]);
  const [tutorInfo, setTutorInfo] = useState(null);
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Sort state
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);

  // Check if we're in "all students" mode
  const isAllMode = tutorId === 'all';

  // Fetch roster data when tutor changes
  useEffect(() => {
    if (tutorId) {
      if (isAllMode) {
        fetchAllStudents();
      } else {
        fetchRoster(tutorId);
      }
    } else {
      setStudents([]);
      setTutorInfo(null);
      setUnassignedCount(0);
    }
  }, [tutorId, isAllMode]);

  // Fetch roster for specific tutor
  const fetchRoster = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/student-roster?tutor_id=${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch roster');
      }
      const data = await response.json();
      setStudents(data.students || []);
      setTutorInfo(data.tutor || null);
      setUnassignedCount(0);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching roster:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL students (admin mode)
  const fetchAllStudents = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/admin/all-students');
      if (!response.ok) {
        throw new Error('Failed to fetch all students');
      }
      const data = await response.json();
      setStudents(data.students || []);
      setTutorInfo(null); // No specific tutor in all mode
      setUnassignedCount(data.unassigned_count || 0);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching all students:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    if (tutorId) {
      if (isAllMode) {
        fetchAllStudents();
      } else {
        fetchRoster(tutorId);
      }
    }
  }, [tutorId, isAllMode]);

  // Extract unique subjects from all students
  const availableSubjects = useMemo(() => {
    const subjectSet = new Set();
    students.forEach(student => {
      if (student.target_subjects) {
        student.target_subjects.split(',').forEach(s => {
          subjectSet.add(s.trim());
        });
      }
    });
    return Array.from(subjectSet).sort();
  }, [students]);

  // Parse subjects helper
  const parseSubjects = (subjectsString) => {
    if (!subjectsString) return [];
    return subjectsString.split(',').map(s => s.trim());
  };

  // Filter students
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      // Subject filter
      if (filters.subject) {
        const studentSubjects = parseSubjects(student.target_subjects);
        if (!studentSubjects.includes(filters.subject)) {
          return false;
        }
      }

      // Assignment status filter (only applicable in "all" mode)
      if (isAllMode && filters.assignmentStatus) {
        const isAssigned = !!student.assigned_tutor_id;
        if (filters.assignmentStatus === 'assigned' && !isAssigned) {
          return false;
        }
        if (filters.assignmentStatus === 'unassigned' && isAssigned) {
          return false;
        }
      }

      // Search filter (searches name, UUID, grade, learning style, tutor name)
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const searchableText = [
          student.first_name,
          student.last_name,
          student.uuid,
          student.grade_year_level,
          student.learning_style,
          student.target_subjects,
          student.assigned_tutor_name
        ].filter(Boolean).join(' ').toLowerCase();

        if (!searchableText.includes(term)) {
          return false;
        }
      }

      // Date from filter
      if (filters.dateFrom) {
        const dateField = student.assigned_at || student.created_date;
        if (!dateField) return false;
        const assignedDate = new Date(dateField);
        const fromDate = new Date(filters.dateFrom);
        if (assignedDate < fromDate) {
          return false;
        }
      }

      // Date to filter
      if (filters.dateTo) {
        const dateField = student.assigned_at || student.created_date;
        if (!dateField) return false;
        const assignedDate = new Date(dateField);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (assignedDate > toDate) {
          return false;
        }
      }

      return true;
    });
  }, [students, filters, isAllMode]);

  // Sort filtered students
  const sortedStudents = useMemo(() => {
    const sorted = [...filteredStudents];

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.field) {
        case 'uuid':
          // Sort by name if available, otherwise by UUID
          aVal = [a.first_name, a.last_name].filter(Boolean).join(' ').trim().toLowerCase() || a.uuid || '';
          bVal = [b.first_name, b.last_name].filter(Boolean).join(' ').trim().toLowerCase() || b.uuid || '';
          break;
        case 'subject':
          aVal = a.target_subjects || '';
          bVal = b.target_subjects || '';
          break;
        case 'sessions':
          aVal = a.session_count || 0;
          bVal = b.session_count || 0;
          break;
        case 'assigned_at':
          aVal = new Date(a.assigned_at || a.created_date || 0).getTime();
          bVal = new Date(b.assigned_at || b.created_date || 0).getTime();
          break;
        case 'grade':
          aVal = a.grade_year_level || '';
          bVal = b.grade_year_level || '';
          break;
        case 'tutor':
          aVal = a.assigned_tutor_name || 'zzz'; // Unassigned sorts last
          bVal = b.assigned_tutor_name || 'zzz';
          break;
        default:
          aVal = a[sortConfig.field] || '';
          bVal = b[sortConfig.field] || '';
      }

      // Handle numeric vs string comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredStudents, sortConfig]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Update sort
  const updateSort = useCallback((field) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        // Toggle direction if same field
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // New field, default to descending
      return { field, direction: 'desc' };
    });
  }, []);

  // Set specific sort configuration
  const setSort = useCallback((config) => {
    setSortConfig(config);
  }, []);

  return {
    // Data
    students: sortedStudents,
    rawStudents: students,
    tutorInfo,
    availableSubjects,
    unassignedCount,

    // Mode
    isAllMode,

    // State
    loading,
    error,

    // Filters
    filters,
    updateFilters,
    resetFilters,

    // Sorting
    sortConfig,
    updateSort,
    setSort,

    // Actions
    refetch
  };
};

export default useStudentRoster;
